import { Autocomplete, TextField } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useDebounce from "../external/ensolvers-core-frontend-mui/hooks/useDebounce";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { AppToolbar, DatePickerWrapper, DatePickerComponent, MIN_DATE, MAX_DATE } from "../components/AppToolbar";
import { SelectComponent } from "../components/SelectComponent";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import { trace } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import { useMaxStoredDate } from "../hooks/useStoredDate";
import VacationReport from "../models/VacationReport";
import ProjectService from "../services/ProjectService";
import TimeoffService from "../services/TimeoffService";
import { RemainingVacancies } from "../utils/enums/RemainingVacancies";
import OpsDateUtils, { Months } from "../utils/OpsDateUtils";
import RoleUtils from "../utils/RoleUtils";
import { ROWS_PER_PAGE } from "../App";
import useWindowSize from "../hooks/useWindowSize";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import { makeStyles } from "@material-ui/core";
import useTableHeight from "../hooks/useTableHeight";
import { useLocation } from "react-router-dom";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { Routes } from "../types/Routes";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";

export default function VacationReportList() {
  const { t } = useTranslation();
  const classes = useStyles();

  const [selectedDate, setSelectedDate] = useMaxStoredDate();
  const [month, setMonth] = useState<string>(Months.ALL);
  const [remainingVacancies, setRemainingVacancies] = useState<string>(RemainingVacancies.ALL);
  const [userNameFilter, setUserNameFilter] = useState<string>("");
  const [projects, setProjects] = useState<AutoCompleteUIDTO[]>([]);
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>({ label: "All", externalId: "All" });
  const debounce = useDebounce(userNameFilter, 2000);
  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();
  const searchParams = new URLSearchParams(useLocation().search);
  const nameFilter = searchParams.get("name");
  const monthFilter = searchParams.get("month");
  const yearFilter = searchParams.get("year");
  const vacanciesFilter = searchParams.get("vacancies");
  const project = searchParams.get("project");
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);

  const {updateUrl} = useUpdateUrl(Routes.VACATION_REPORT, searchParams);

  useEffect(() => {
    getProjectNames();
  }, []);

  useEffect(() => {
    const date = new Date(selectedDate);

    if (monthFilter !== null) {
      setMonth(monthFilter);
    } else {
      searchParams.set("month", month);
    }

    if (yearFilter !== null) {
      date.setFullYear(parseInt(yearFilter));
      setSelectedDate(date);
    } else {
      searchParams.set("year", date.getFullYear().toString());
    }

    if (project !== null && projects) {
      const projectUrl = projects.find((p) => p.externalId === project);
      if (projectUrl) setSelectedProject(projectUrl);
    } else if (storedProject !== null && projects) {
      const projectFilter = projects.find((p) => p.externalId === storedProject.externalId);
      if (projectFilter) setSelectedProject(projectFilter);
      searchParams.set("project", storedProject.externalId);
    } else {
      searchParams.set("project", "All");
    }

    if (vacanciesFilter !== null) {
      setRemainingVacancies(vacanciesFilter);
    } else {
      searchParams.set("vacancies", "All");
    }

    if (nameFilter !== null && nameFilter !== "") {
      setUserNameFilter(nameFilter);
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const getProjectNames = () => {
    if (RoleUtils.isAdmin()) {
      ProjectService.findProjectNames()
        .then((result) => {
          setProjects(result.getContent());
        })
        .catch((error) => trace("Error getting project names", error));
    } else if (RoleUtils.isManager()) {
      ProjectService.findProjectsNamesByUserManagerExternalId()
        .then((result) => {
          setProjects(result.getContent());
        })
        .catch((error) => trace("Error getting project names", error));
    }
  };

  const getContent = async (page: number, pageSize: number) => {
    const result = await TimeoffService.getVacationReport(
      selectedDate.getFullYear(),
      Object.keys(Months).indexOf(month.toUpperCase()),
      page,
      pageSize,
      debounce,
      vacanciesFilter ? vacanciesFilter : remainingVacancies,
      project ? project : selectedProject?.externalId || "All"
    );
    const resultContent = result.succeeded() ? result.getContent() : PagedResult.empty([]);
    return resultContent;
  };

  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        <TextField
          name="username"
          label="User name"
          value={userNameFilter}
          onChange={(e) => {
            setUserNameFilter(e.target.value);
            searchParams.set("name", e.target.value);
            updateUrl();
          }}
          autoComplete="off"
          size="small"
        />
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        <TextField
          name="username"
          label="User name"
          value={userNameFilter}
          onChange={(e) => {
            setUserNameFilter(e.target.value);
            searchParams.set("name", e.target.value);
            updateUrl();
          }}
          autoComplete="off"
          size="small"
        />
        {renderDialogFilters}
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      <SelectComponent
        className="date-picker"
        name="month"
        label={"Month"}
        value={month}
        setValue={(value) => {
          setMonth(value);
          searchParams.set("month", value);
          updateUrl();
        }}
        options={Object.values(Months)}
        customStyle={{ margin: "0 0.5em 0 1.5em" }}
        size="small"
      />
      <DatePickerWrapper>
        <DatePickerComponent
          className="date-picker"
          test-id="year"
          views="year"
          label="Year"
          value={selectedDate}
          inputFormat="yyyy"
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          onChange={(selectedDate) => {
            setSelectedDate(selectedDate!);
            searchParams.set("year", selectedDate!.getFullYear().toString());
            updateUrl();
          }}
        />
      </DatePickerWrapper>
      <SelectComponent
        name="vacancies-left"
        label={"Vacancies Left"}
        value={remainingVacancies}
        setValue={(value) => {
          setRemainingVacancies(value);
          searchParams.set("vacancies", value);
          updateUrl();
        }}
        options={Object.values(RemainingVacancies)}
        size="small"
      />
      <Autocomplete
        disablePortal
        options={[{ label: "All", externalId: "All" }, ...projects]}
        getOptionLabel={(option) => option.label}
        sx={{ minWidth: 300, marginLeft: "10px" }}
        size="small"
        value={selectedProject}
        onChange={(event, newValue) => {
          if (newValue) {
            setSelectedProject(newValue);
            searchParams.set("project", newValue.externalId);
            setStoredProject(newValue);
            updateUrl();
          }
        }}
        renderInput={(params) => <TextField {...params} name="project-name" label="Project Name" variant="outlined" />}
        disableClearable
      />
    </>
  );

  const getHeader = () => {
    return <AppToolbar title={t("Vacation report")}>{renderFilters()}</AppToolbar>;
  };

  const getBody = () => {
    return (
      <>
        <TableBasedCrud<VacationReport>
          elementName={t("Vacation report")}
          maxHeight={calculateMaxHeight(false)}
          stickyHeader
          denseTable

          onDataFetch={(page, count) => getContent(page, count)}
          enablePagination
          delayFetch={userNameFilter}
          rowsPerPage={ROWS_PER_PAGE}
          tableColumns={[
            {
              header: t("Team member"),
              content: (instance) => <>{instance.teamMember}</>,
            },
            {
              header: t("Joined"),
              content: (instance) => <>{instance.shortStart ? OpsDateUtils.formatInUTCAsDDMMMYYY(instance.shortStart) : "-"}</>,
            },
            {
              header: t("Vacation Accumulation per Year"),
              content: (instance) => <>{Number(instance.vacationAccumulation)}</>,
            },
            {
              header: t("Additional Vacations Amount"),
              content: (instance) => (
                <>{instance.additionalVacationsAmount ? Number(instance.additionalVacationsAmount.toFixed(2)) : "-"}</>
              ),
            },
            {
              header: t("Vacations Computed Since"),
              content: (instance) => (
                <>{instance.vacationsComputedSince ? OpsDateUtils.formatInUTCAsDDMMMYYY(instance.vacationsComputedSince) : "-"}</>
              ),
            },
            {
              header: t("Initial Taken Vacations"),
              content: (instance) => <>{instance.initialTakenVacations ? Number(instance.initialTakenVacations) : "-"}</>,
            },
            {
              header: t("Accumulated"),
              content: (instance) => <>{instance.vacationsAccumulated ? Number(instance.vacationsAccumulated.toFixed(2)) : "-"}</>,
            },
            {
              header: t("Approved"),
              content: (instance) => <>{instance.vacationsApproved ? Number(instance.vacationsApproved.toFixed(2)) : "-"}</>,
            },
            {
              header: t("Left"),
              content: (instance) => <>{instance.vacationsLeft ? Number(instance.vacationsLeft.toFixed(2)) : "-"}</>,
            },
            {
              header: t("Pending"),
              content: (instance) => <>{instance.vacationsPending ? Number(instance.vacationsPending.toFixed(2)) : "-"}</>,
            },
          ]}
          renderDialogContent={() => <></>}
          hideActions
        />

        {/* Dialog to manage filters  */}
        <FilterDialog title={"Vacation "} setIsOpen={setIsOpen} isOpen={isOpen}>
          {renderDialogFilters}
        </FilterDialog>
      </>
    );
  };

  return <AppContainerFullHeight bottomSectionRef={bottomSectionRef} topSection={getHeader()} bottomSection={getBody()} />;
}

const useStyles = makeStyles((theme) => ({
  desktopFilters: {
    padding: "5px",
    display: "flex",
    flexDirection: "row",
  },
  mobileFilters: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  [theme.breakpoints.down("md")]: {
    desktopFilters: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gridRowGap: "10px",
      gridColumnGap: "10px",
      "&> div": {
        margin: "0!important",
        minWidth: "0",
      },
      "& .date-picker": {
        margin: "0 !important",
        width: "100%  !important",
        minWidth: "100px !important",
      },
    },
  },
}),
{ index: 1 }
);
